import React, { useState, useEffect } from 'react';
// useAuth is removed as it's not being used
import { sourceService } from '../../../services/sourceService';
import { categoryService } from '../../../services/categoryService';
import { groupService } from '../../../services/groupService';
import { typeService } from '../../../services/typeService';
import { tagService } from '../../../services/tagService';
import { PencilIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline';
import { Dialog, DialogContent, DialogTitle } from '../../ui/custom/Dialog';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useTheme } from '../../../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';

interface MetadataItem {
  id: number;
  name: string;
  description: string | null;
  is_active: boolean;
}

interface ColumnHeader {
  label: string;
  className: string;
}

interface MetadataManagerProps {
  title: string;
  type: 'agents' | 'sources' | 'categories' | 'types' | 'groups' | 'tags';
  description?: string;
  renderExtraActions?: (item: any) => React.ReactNode;
  renderExtraColumns?: (item: any) => React.ReactNode;
  extraColumnHeaders?: ColumnHeader[];
}

export default function MetadataManager({
  title,
  type,
  description,
  renderExtraActions,
  renderExtraColumns,
  extraColumnHeaders = []
}: MetadataManagerProps) {
  // Auth user removed as it's not being used
  const { theme } = useTheme();
  const { t } = useTranslation();
  const [items, setItems] = useState<MetadataItem[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<MetadataItem | null>(null);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [error, setError] = useState('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<MetadataItem | null>(null);

  useEffect(() => {
    fetchItems();
  }, [type]);

  const fetchItems = async () => {
    try {
      let response;
      switch (type) {
        case 'sources':
          response = await sourceService.getAllSources();
          break;
        case 'categories':
          response = await categoryService.getAllCategories();
          break;
        case 'groups':
          response = await groupService.getAllGroups();
          break;
        case 'types':
          response = await typeService.getAllTypes();
          break;
        case 'tags':
          response = await tagService.getAllTags();
          break;
      }
      setItems((response as MetadataItem[]) || []);
    } catch (error) {
      setError(t('metadata.failedToFetch'));
      console.error('Error fetching items:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      if (editingItem) {
        switch (type) {
          case 'sources':
            await sourceService.updateSource(editingItem.id, formData.name, formData.description);
            break;
          case 'categories':
            await categoryService.updateCategory(
              editingItem.id,
              formData.name,
              formData.description
            );
            break;
          case 'groups':
            await groupService.updateGroup(editingItem.id, formData.name, formData.description);
            break;
          case 'types':
            await typeService.updateType(editingItem.id, formData.name, formData.description);
            break;
          case 'tags':
            await tagService.updateTag(editingItem.id, formData.name, formData.description);
            break;
        }
      } else {
        switch (type) {
          case 'sources':
            await sourceService.createSource(formData.name, formData.description);
            break;
          case 'categories':
            await categoryService.createCategory(formData.name, formData.description);
            break;
          case 'groups':
            await groupService.createGroup(formData.name, formData.description);
            break;
          case 'types':
            await typeService.createType(formData.name, formData.description);
            break;
          case 'tags':
            await tagService.createTag(formData.name, formData.description);
            break;
        }
      }

      setIsModalOpen(false);
      setEditingItem(null);
      setFormData({ name: '', description: '' });
      fetchItems();
    } catch (error) {
      setError(t('metadata.failedToSave'));
      console.error('Error saving item:', error);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      switch (type) {
        case 'sources':
          await sourceService.deleteSource(id);
          break;
        case 'categories':
          await categoryService.deleteCategory(id);
          break;
        case 'groups':
          await groupService.deleteGroup(id);
          break;
        case 'types':
          await typeService.deleteType(id);
          break;
        case 'tags':
          await tagService.deleteTag(id);
          break;
      }
      setIsDeleteModalOpen(false);
      setItemToDelete(null);
      fetchItems();
    } catch (error) {
      setError(t('metadata.failedToDelete'));
      console.error('Error deleting item:', error);
    }
  };

  const openEditModal = (item: MetadataItem) => {
    setEditingItem(item);
    setFormData({ name: item.name, description: item.description || '' });
    setIsModalOpen(true);
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className={`p-4 rounded-md ${theme === 'dark' ? 'bg-red-900/30' : 'bg-red-50'}`}>
          <p className={`text-sm ${theme === 'dark' ? 'text-red-200' : 'text-red-700'}`}>{error}</p>
        </div>
      )}

      <div className="flex justify-between items-center mb-4">
        <div>
          <h2
            className={`text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}
          >
            {title}
          </h2>
          {description && (
            <p className={`mt-1 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
              {description}
            </p>
          )}
        </div>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => {
              setEditingItem(null);
              setFormData({ name: '', description: '' });
              setIsModalOpen(true);
            }}
            className={`inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white ${
              theme === 'dark'
                ? 'bg-indigo-600 hover:bg-indigo-500'
                : 'bg-indigo-600 hover:bg-indigo-700'
            }`}
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
            {t('metadata.addNew')}
          </button>
        </div>
      </div>

      <div className="mt-4">
        <div className="overflow-x-auto">
          <table
            className={`min-w-full divide-y ${
              theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'
            }`}
          >
            <thead className={theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'}>
              <tr>
                <th
                  className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  }`}
                >
                  {t('metadata.name')}
                </th>
                <th
                  className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  }`}
                >
                  {t('metadata.description')}
                </th>
                {extraColumnHeaders?.map((header, index) => (
                  <th
                    key={index}
                    className={`${header.className} ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}
                  >
                    {header.label}
                  </th>
                ))}
                <th
                  className={`relative px-6 py-3 text-xs font-medium uppercase tracking-wider ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  }`}
                >
                  {t('metadata.actions')}
                </th>
              </tr>
            </thead>
            <tbody
              className={
                theme === 'dark'
                  ? 'bg-gray-800 divide-y divide-gray-700'
                  : 'bg-white divide-y divide-gray-200'
              }
            >
              {items.map((item) => (
                <tr key={item.id}>
                  <td
                    className={`px-6 py-4 whitespace-nowrap text-sm ${
                      theme === 'dark' ? 'text-gray-200' : 'text-gray-900'
                    }`}
                  >
                    {item.name}
                  </td>
                  <td
                    className={`px-6 py-4 whitespace-nowrap text-sm ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                    }`}
                  >
                    {item.description || t('common.none')}
                  </td>
                  {renderExtraColumns && renderExtraColumns(item)}
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                    {renderExtraActions && renderExtraActions(item)}
                    <button
                      onClick={() => openEditModal(item)}
                      className={
                        theme === 'dark'
                          ? 'text-indigo-400 hover:text-indigo-300'
                          : 'text-indigo-600 hover:text-indigo-900'
                      }
                    >
                      <PencilIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => {
                        setItemToDelete(item);
                        setIsDeleteModalOpen(true);
                      }}
                      className={
                        theme === 'dark'
                          ? 'text-red-400 hover:text-red-300'
                          : 'text-red-600 hover:text-red-900'
                      }
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className={theme === 'dark' ? 'bg-gray-800 border-gray-700' : undefined}>
          <DialogTitle className={theme === 'dark' ? 'text-gray-100' : undefined}>
            {editingItem ? t('metadata.edit') : t('metadata.addNew')} {title.slice(0, -1)}
          </DialogTitle>
          <form onSubmit={handleSubmit}>
            <div className="mt-4">
              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="name"
                    className={`block text-sm font-medium ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}
                  >
                    {t('metadata.name')}
                  </label>
                  <input
                    type="text"
                    name="name"
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className={`mt-1 block w-full rounded-md shadow-sm py-2 px-3 sm:text-sm ${
                      theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 text-gray-100 focus:ring-indigo-500 focus:border-indigo-500'
                        : 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'
                    }`}
                    required
                  />
                </div>
                <div>
                  <label
                    htmlFor="description"
                    className={`block text-sm font-medium ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}
                  >
                    {t('metadata.description')}
                  </label>
                  <textarea
                    name="description"
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className={`mt-1 block w-full rounded-md shadow-sm py-2 px-3 sm:text-sm ${
                      theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 text-gray-100 focus:ring-indigo-500 focus:border-indigo-500'
                        : 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'
                    }`}
                    rows={3}
                  />
                </div>
              </div>
            </div>
            <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
              <button
                type="submit"
                className={`inline-flex w-full justify-center rounded-md border border-transparent px-4 py-2 text-base font-medium text-white shadow-sm focus:outline-none focus:ring-2 sm:ml-3 sm:w-auto sm:text-sm ${
                  theme === 'dark'
                    ? 'bg-indigo-600 hover:bg-indigo-500 focus:ring-offset-gray-800 focus:ring-indigo-500'
                    : 'bg-indigo-600 hover:bg-indigo-700 focus:ring-offset-2 focus:ring-indigo-500'
                }`}
              >
                {editingItem ? t('metadata.save') : t('metadata.create')}
              </button>
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className={`mt-3 inline-flex w-full justify-center rounded-md border px-4 py-2 text-base font-medium shadow-sm focus:outline-none focus:ring-2 sm:mt-0 sm:w-auto sm:text-sm ${
                  theme === 'dark'
                    ? 'border-gray-600 bg-gray-700 text-gray-200 hover:bg-gray-600 focus:ring-offset-gray-800 focus:ring-indigo-500'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:ring-offset-2 focus:ring-indigo-500'
                }`}
              >
                {t('common.cancel')}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <DialogContent className={theme === 'dark' ? 'bg-gray-800 border-gray-700' : undefined}>
          <DialogTitle className={theme === 'dark' ? 'text-gray-100' : undefined}>
            {t('metadata.confirmDelete')}
          </DialogTitle>
          <div className="py-4">
            <div className="flex items-center">
              <ExclamationTriangleIcon
                className={`h-6 w-6 ${theme === 'dark' ? 'text-red-400' : 'text-red-600'}`}
              />
              <p className={`ml-3 text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                {t('metadata.deleteConfirmation', { name: itemToDelete?.name })}
              </p>
            </div>
            <div className="mt-4 flex justify-end space-x-3">
              <button
                onClick={() => setIsDeleteModalOpen(false)}
                className={`px-4 py-2 text-sm font-medium rounded-md ${
                  theme === 'dark'
                    ? 'text-gray-300 hover:text-gray-200 bg-gray-700 hover:bg-gray-600'
                    : 'text-gray-700 hover:text-gray-900 bg-gray-100 hover:bg-gray-200'
                }`}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={() => itemToDelete && handleDelete(itemToDelete.id)}
                className={`px-4 py-2 text-sm font-medium rounded-md text-white ${
                  theme === 'dark' ? 'bg-red-600 hover:bg-red-500' : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {t('metadata.delete')}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
